import * as React from 'react';

/** Material UI Packages. */
import { Checkbox, Fade, IconButton, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';

/** Material UI Icons. */
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import useUserRole from 'shared/hooks/useUserRole';
import { formatOperationDateTime } from 'shared/lib/dateFormatter';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

export default function CommentContent(props) {
    /** Get props from CommentBox.js. */
    const { comment, parentType, editMode, handlers } = props;
    /** Destructuring values from handlers. */
    const { handleDeleteClick, handleEditClick, handleReplyClick, handleStatusClick } = handlers;
    /** Destructuring values from useAuth0 hook. */
    const { user } = useAuth0();

    const userEmail = user.email;
    const normalizeEmail = (email) => email?.trim().toLowerCase() ?? '';

    /** Grant permission if the user is an assistant manager or above, or the comment author. */
    const isAuthorized =
        isAssistantManagerOrAbove(useUserRole()) ||
        (normalizeEmail(userEmail) !== '' && normalizeEmail(userEmail) === normalizeEmail(comment.registered_by_email));

    /** 코멘트를 남긴 시각을 근무지(LA) 기준으로 보여준다. 한국에서 봐도 같은 숫자다. */
    const convertToOperationTime = (utc) => formatOperationDateTime(utc, 'MM/DD/YYYY, HH:mm');

    /** Check if a comment is being edited. */
    const isEditing = editMode.active && editMode.editRow.id === comment.id;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {comment.sub_id === 0 ? null : (
                    <SubdirectoryArrowRightIcon sx={{ color: parentType === 'todo' ? '#ea4c89' : '#F6BD60' }} />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
                    <Box sx={{ mt: comment.il_type === null ? 0.5 : 0 }}>
                        <Typography sx={{ fontSize: '12px', flexGrow: 1 }}>
                            <strong>{comment.registered_by}</strong>, {convertToOperationTime(comment.modified_on)}{' '}
                        </Typography>
                    </Box>
                    <Box>
                        {comment.il_type === 'todo' && isAuthorized ? (
                            <>
                                <Fade in={comment.todo_status === true}>
                                    <Typography
                                        sx={{
                                            display: 'inline-block',
                                            fontSize: '11px',
                                            mr: 0.5,
                                            fontStyle: 'italic',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Complete!
                                    </Typography>
                                </Fade>
                                <Tooltip title="Complete">
                                    <Checkbox
                                        size="small"
                                        checked={comment.todo_status === true}
                                        onClick={() => handleStatusClick(comment)}
                                        sx={{ p: 0 }}
                                    />
                                </Tooltip>
                            </>
                        ) : null}
                        {comment.il_type === null ? null : (
                            <Tooltip title="Reply">
                                <IconButton size="small" onClick={() => handleReplyClick(comment)}>
                                    <ReplyIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isAuthorized ? (
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditClick(comment)}>
                                    <EditIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {isAuthorized ? (
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDeleteClick(comment)}>
                                    <DeleteIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
            </Box>
            <Typography
                sx={{
                    whiteSpace: 'pre-line',
                    fontSize: '12px',
                    ml: comment.sub_id !== 0 ? '24px' : 0,
                    mb: 0.5,
                    px: 1,
                    textDecoration: isEditing ? 'underline' : null,
                    fontStyle: isEditing ? 'italic' : null,
                    fontWeight: isEditing ? 'bold' : null,
                    color: isEditing
                        ? parentType === 'todo' || comment.il_type === 'todo'
                            ? '#ea4c89'
                            : '#F6BD60'
                        : null,
                }}
            >
                {comment.content}
            </Typography>
        </Box>
    );
}
